/**
 * Caché genérico en memoria para páginas de estadísticas (Estadísticas, Loyalty, Analítica COD, etc.).
 * Misma idea que dashboardStatsCache pero reutilizable: clave string libre, TTL único.
 * Uso: la página construye la clave (ej. "cod-analytics|tenantId") y guarda/recupera el payload que necesite.
 */

const TTL_MS = 90_000; // 90s (memoria; revalidación en segundo plano en las páginas)

/** Listas ListView: también en sessionStorage para F5 / nueva pestaña (hasta 15 min). */
const SESSION_STORAGE_PREFIX = 'sf-spc:';
const SESSION_LISTVIEW_MAX_AGE_MS = 15 * 60 * 1000;

interface CacheEntry<T> {
  payload: T;
  expiresAt: number;
}

const store = new Map<string, CacheEntry<unknown>>();

function isListViewCacheKey(key: string): boolean {
  return key.startsWith('listview|');
}

function readListViewFromSession<T>(key: string): T | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { payload: T; storedAt: number };
    if (Date.now() - parsed.storedAt > SESSION_LISTVIEW_MAX_AGE_MS) {
      sessionStorage.removeItem(SESSION_STORAGE_PREFIX + key);
      return null;
    }
    return parsed.payload;
  } catch {
    return null;
  }
}

function writeListViewToSession<T>(key: string, payload: T): void {
  if (typeof sessionStorage === 'undefined' || !isListViewCacheKey(key)) return;
  try {
    sessionStorage.setItem(
      SESSION_STORAGE_PREFIX + key,
      JSON.stringify({ payload, storedAt: Date.now() })
    );
  } catch {
    // QuotaExceeded u origen privado
  }
}

/** Solo memoria (evita recursión con setStatsPageCache). */
function primeMemoryCache<T>(key: string, payload: T): void {
  store.set(key, { payload, expiresAt: Date.now() + TTL_MS });
}

export function getStatsPageCache<T>(key: string): T | null {
  const entry = store.get(key) as CacheEntry<T> | undefined;
  if (entry && Date.now() <= entry.expiresAt) return entry.payload;
  if (entry && Date.now() > entry.expiresAt) {
    store.delete(key);
  }

  if (isListViewCacheKey(key)) {
    const fromSession = readListViewFromSession<T>(key);
    if (fromSession != null) {
      primeMemoryCache(key, fromSession);
      return fromSession;
    }
  }

  return null;
}

export function setStatsPageCache<T>(key: string, payload: T): void {
  primeMemoryCache(key, payload);
  if (isListViewCacheKey(key)) {
    writeListViewToSession(key, payload);
  }
}

/** Quita una entrada (útil si el detalle dejó de existir o tras mutaciones que invalidan la clave). */
export function invalidateStatsPageCache(key: string): void {
  store.delete(key);
  if (isListViewCacheKey(key) && typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_PREFIX + key);
    } catch {
      // ignore
    }
  }
}

/** Clave para Analítica COD (solo tenant). */
export function codAnalyticsCacheKey(tenantId: string): string {
  return `cod-analytics|${tenantId}`;
}

/** Clave para Loyalty Dashboard (solo tenant). */
export function loyaltyDashboardCacheKey(tenantId: string): string {
  return `loyalty-dashboard|${tenantId}`;
}

/** Clave para Estadísticas (tenant + rango + canal + branch opcional). */
export function statisticsCacheKey(
  tenantId: string,
  start: string,
  end: string,
  channel: string,
  branchId?: string | null
): string {
  return `statistics|${tenantId}|${start}|${end}|${channel}|${branchId ?? ''}`;
}

/** Clave para Clientes (tenant + branch + rango fechas). */
export function customersCacheKey(
  tenantId: string,
  branchId: string | null,
  start: string,
  end: string
): string {
  return `customers|${tenantId}|${branchId ?? ''}|${start}|${end}`;
}

/** Clave para Productos (solo tenant). */
export function productsCacheKey(tenantId: string): string {
  return `products|${tenantId}`;
}

/** Clave genérica para listas ListView (categorías, modificadores, tags, etc.). */
export function listViewCacheKey(pageKey: string, tenantId: string): string {
  return `listview|${pageKey}|${tenantId}`;
}

/**
 * Lista con filtros variables (CRM leads/kanban, etc.). Persiste en sessionStorage como listview.
 * `filterParts` debe ser estable (mismo orden) entre renders.
 */
export function listViewFilterCacheKey(pageKey: string, tenantId: string, filterParts: string[]): string {
  const scope = filterParts.map((p) => encodeURIComponent(p ?? '')).join('\u001e');
  return `listview|${pageKey}|${tenantId}|${scope}`;
}

/** Extensiones Llamadas (por tenant). */
export function callCenterExtensionsCacheKey(tenantId: string): string {
  return listViewCacheKey('call-center-extensions', tenantId);
}

/** Campañas Llamadas (por tenant). */
export function callCenterCampaignsCacheKey(tenantId: string): string {
  return listViewCacheKey('call-center-campaigns', tenantId);
}

/** Historial de llamadas CRM (lista reciente por tenant). */
export function crmCallsHistoryCacheKey(tenantId: string): string {
  return listViewCacheKey('crm-calls-history', tenantId);
}

/**
 * Estadísticas de Llamadas: depende del rango de fechas (solo memoria + TTL; no listview session).
 */
export function callCenterStatsCacheKey(tenantId: string, rangeStartIso: string, rangeEndIso: string): string {
  return `call-center-stats|${tenantId}|${rangeStartIso}|${rangeEndIso}`;
}

/** Configuración VoIP / Llamadas en Ajustes (filas tenant_voip_config). */
export function callCenterSettingsCacheKey(tenantId: string): string {
  return listViewCacheKey('call-center-settings', tenantId);
}

/** Detalle de entidad (solo memoria TTL, sin sessionStorage). */
export function adminDetailCacheKey(kind: string, tenantId: string, entityId: string): string {
  return `admin-detail|${kind}|${tenantId}|${entityId}`;
}
